import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { quantityInWords } from '@erp/shared';
import type { OrganizationBankAccount } from '../api/organizations';
import { powersOfAttorneyApi, type PowerOfAttorney } from '../api/powers-of-attorney';

/**
 * Типовые межотраслевые формы М-2 и М-2а, утверждённые постановлением
 * Госкомстата России от 30.10.97 № 71а. Вёрстка повторяет типографский бланк.
 *
 * Различие форм ровно одно: у М-2 сверху отрывной корешок с графами 1–8 и
 * линией отреза, у М-2а его нет — там регистрация идёт в журнале учёта
 * доверенностей. Остальное содержимое идентично.
 */
type FormKind = 'M2' | 'M2A';

const FORM_META: Record<FormKind, { title: string; okud: string }> = {
  M2: { title: 'Типовая межотраслевая форма №  М-2', okud: '0315001' },
  M2A: { title: 'Типовая межотраслевая форма № М-2а', okud: '0315002' },
};

const MONTHS_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

function formatDateShort(value: string | null): string {
  if (!value) return '';
  return new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** «18 сентября 2025 г.» — формат дат в заполненном бланке. */
function formatDateLong(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  return `${date.getDate()} ${MONTHS_GENITIVE[date.getMonth()]} ${date.getFullYear()} г.`;
}

/**
 * Количество в графе 4: «10 (Десять)» — цифрой и прописью, как в бланке.
 * Дробные значения прописью не раскрываем: «Сто пятьдесят» для 150.5 сбивало бы
 * с толку, поэтому у них остаётся только цифровая запись.
 */
function formatQuantityCell(quantity: string): string {
  const numeric = Number(quantity);
  const digits = Number.isInteger(numeric) ? String(numeric) : quantity.replace(/0+$/, '').replace(/\.$/, '');
  if (!Number.isInteger(numeric)) return digits;
  return `${digits} (${quantityInWords(quantity)})`;
}

/** «Иванов Иван Иванович» → «Иванов И. И.» — расшифровка подписи в бланке сокращённая. */
function abbreviateName(value: string | null): string {
  if (!value) return '';
  const [last, ...rest] = value.trim().split(/\s+/);
  if (!last || rest.length === 0) return value.trim();
  return `${last} ${rest.map((part) => `${part.charAt(0).toUpperCase()}.`).join(' ')}`;
}

/** Документ-основание одной строкой: «СЧЕТ № 1859 от 16 Сентября 2025 г.». */
function basisDocument(poa: PowerOfAttorney): string {
  const parts = [poa.basisDocName];
  if (poa.basisDocNumber) parts.push(`№ ${poa.basisDocNumber}`);
  if (poa.basisDocDate) parts.push(`от ${formatDateLong(poa.basisDocDate)}`);
  return parts.filter(Boolean).join(' ');
}

/**
 * Графы «потребитель» и «плательщик» — одной строкой с ИНН и полным адресом:
 * «ИП Шилов…, ИНН 590612219505, 614010, Пермский край, г Пермь, ул …».
 */
function organizationOneLine(org: { fullName: string; inn: string | null; addressFull: string | null }): string {
  const parts = [org.fullName];
  if (org.inn) parts.push(`ИНН ${org.inn}`);
  if (org.addressFull?.trim()) parts.push(org.addressFull.trim());
  return parts.join(',  ');
}

/**
 * Графа «Организация» в бланке двухстрочная: наименование, ИНН и почтовый
 * индекс на первой строке, остальной адрес на второй. Индекс отделяем от
 * адреса, потому что он хранится первым элементом строки addressFull.
 */
function organizationLines(org: {
  fullName: string;
  inn: string | null;
  addressFull: string | null;
}): [string, string] {
  const head = [org.fullName];
  if (org.inn) head.push(`ИНН ${org.inn}`);
  const address = org.addressFull?.trim() ?? '';

  const postalMatch = address.match(/^(\d{6})\s*,\s*(.*)$/);
  if (postalMatch) {
    head.push(postalMatch[1]!);
    return [head.join(',  '), postalMatch[2]!];
  }

  return [head.join(',  '), address];
}

/**
 * Банковские реквизиты одной строкой, как в заполненном бланке:
 * «р/с 40802…, в банке ВОЛГО-ВЯТСКИЙ БАНК ПАО СБЕРБАНК, БИК 042202603, к/с 30101…».
 * Части без значения пропускаются, чтобы не печатать «БИК» с пустотой.
 */
interface IdentityDocument {
  label: string;
  series: string;
  number: string;
  issuedBy: string;
  issueDate: string;
}

/**
 * Какой документ печатать в бланке. На водителя выписывают доверенность по
 * водительскому удостоверению — если оно заполнено, печатаем его; иначе
 * паспорт. Пустые графы допустимы: их дозаполняют от руки.
 */
function identityDocument(employee: {
  passportSeries: string | null;
  passportNumber: string | null;
  passportIssuedBy: string | null;
  passportIssueDate: string | null;
  driverLicenseSeries: string | null;
  driverLicenseNumber: string | null;
  driverLicenseIssuedBy: string | null;
  driverLicenseIssueDate: string | null;
  isDriver: boolean;
}): IdentityDocument {
  const hasLicense = !!(employee.driverLicenseSeries && employee.driverLicenseNumber);

  if (hasLicense) {
    return {
      label: 'Водительское удостоверение',
      series: employee.driverLicenseSeries ?? '',
      number: employee.driverLicenseNumber ?? '',
      issuedBy: employee.driverLicenseIssuedBy ?? '',
      issueDate: employee.driverLicenseIssueDate ? formatDateShort(employee.driverLicenseIssueDate) : '',
    };
  }

  return {
    label: 'Паспорт: серия',
    series: employee.passportSeries ?? '',
    number: employee.passportNumber ?? '',
    issuedBy: employee.passportIssuedBy ?? '',
    issueDate: employee.passportIssueDate ? formatDateShort(employee.passportIssueDate) : '',
  };
}

function formatBankDetails(account: OrganizationBankAccount | null): string {
  if (!account) return '';
  const parts = [`р/с ${account.accountNumber}`];
  if (account.bankName) parts.push(`в банке ${account.bankName}`);
  if (account.bik) parts.push(`БИК ${account.bik}`);
  if (account.corrAccount) parts.push(`к/с ${account.corrAccount}`);
  return parts.join(', ');
}

/**
 * PDF собирается «скриншотом» отрисованного бланка (html2canvas → jsPDF), как
 * в печатных формах счёта и УПД: то, что на экране, и есть то, что в файле,
 * без второй вёрстки под PDF-библиотеку.
 */
async function downloadPoaPdf(element: HTMLElement, filename: string): Promise<void> {
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    ignoreElements: (el) => el.classList.contains('no-print'),
  });

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgHeight = (canvas.height * pageWidth) / canvas.width;
  const imgData = canvas.toDataURL('image/png');

  // Многостраничная разбивка на случай длинного перечня ценностей: одно и то же
  // изображение сдвигается вверх на каждой следующей странице, jsPDF обрезает
  // лишнее по границам листа.
  let heightLeft = imgHeight;
  let position = 0;
  pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
  heightLeft -= pageHeight;
  while (heightLeft > 0) {
    position -= pageHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
    heightLeft -= pageHeight;
  }

  pdf.save(filename);
}

export function PowerOfAttorneyPrintPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const autoDownload = searchParams.get('download') === '1';

  const initialForm: FormKind = searchParams.get('form')?.toLowerCase() === 'm2a' ? 'M2A' : 'M2';
  const [form, setForm] = useState<FormKind>(initialForm);
  const [downloading, setDownloading] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);

  const poaQuery = useQuery({
    queryKey: ['power-of-attorney', id],
    queryFn: () => powersOfAttorneyApi.get(id!),
    enabled: !!id,
  });

  const poa = poaQuery.data?.powerOfAttorney;

  async function handleDownload() {
    if (!sheetRef.current || !poa) return;
    setDownloading(true);
    try {
      await downloadPoaPdf(sheetRef.current, `Доверенность № ${poa.number} от ${formatDateShort(poa.date)}.pdf`);
    } finally {
      setDownloading(false);
    }
  }

  useEffect(() => {
    if (!poa) return;
    const timer = setTimeout(() => {
      if (autoDownload) void handleDownload();
      else window.print();
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poa, autoDownload]);

  if (!poa) {
    return <div style={{ padding: 24, fontFamily: 'Times New Roman, serif' }}>Загрузка…</div>;
  }

  const org = poa.organization;
  const meta = FORM_META[form];
  const account = org.bankAccounts?.[0] ?? null;
  const bankDetails = formatBankDetails(account);
  const [orgHead, orgAddress] = organizationLines(org);
  const identity = identityDocument(poa.employee);

  // Потребитель и плательщик — своя организация: доверенность выписывают, чтобы
  // получить оплаченные самим же покупателем ценности. Отдельных полей в
  // документе нет, подставляем реквизиты организации.
  const selfOneLine = organizationOneLine(org);

  return (
    <div className="poa-root">
      <style>{`
        @page { size: A4; margin: 12mm; }
        body { background: #ddd; }
        .poa-root { font-family: 'Times New Roman', Times, serif; color: #000; }
        .poa-sheet {
          width: 210mm;
          margin: 16px auto;
          padding: 12mm;
          background: #fff;
          box-sizing: border-box;
          font-size: 11.5px;
          line-height: 1.25;
        }
        .poa-root table { border-collapse: collapse; }

        /* --- Корешок М-2: горизонтальная таблица с графами 1-8 --- */
        .stub-table { width: 100%; }
        .stub-table td, .stub-table th {
          border: 1px solid #000;
          padding: 2px 4px;
          font-size: 10px;
          text-align: center;
          vertical-align: middle;
        }
        .stub-table .num-row td { font-size: 9px; }
        .stub-table .data-row td { height: 26px; text-align: left; font-size: 10px; }
        .cut-line { text-align: center; font-size: 10px; margin: 6px 0 10px; border-bottom: 1px dashed #000; padding-bottom: 2px; }

        /* --- Шапка формы --- */
        .form-meta { text-align: right; font-size: 9.5px; line-height: 1.3; }
        .okud-block { display: flex; justify-content: flex-end; align-items: flex-start; margin-top: 2px; }
        .okud-labels { text-align: right; font-size: 10.5px; line-height: 1.5; padding-right: 4px; }
        .okud-cells { border: 1px solid #000; width: 92px; font-size: 10px; }
        .okud-cells .cell { border-bottom: 1px solid #000; text-align: center; font-size: 9px; padding: 0 2px; }
        .okud-cells .cell:last-child { border-bottom: none; }
        .okud-cells .value { text-align: center; font-weight: 700; padding: 1px 2px; min-height: 14px; }

        /* --- Поля бланка --- */
        .f-row { display: flex; align-items: flex-end; gap: 4px; margin-top: 7px; }
        .f-label { flex: none; padding-bottom: 1px; }
        .f-fill { flex: 1; min-width: 0; display: flex; flex-direction: column; }
        .f-value { border-bottom: 1px solid #000; min-height: 15px; padding: 0 4px 1px; }
        .f-caption { font-size: 8.5px; text-align: center; line-height: 1.2; padding-top: 1px; }

        .doc-title { font-weight: 700; font-size: 13px; }

        /* --- Таблица ценностей --- */
        .items { width: 100%; margin-top: 4px; }
        .items th, .items td { border: 1px solid #000; padding: 3px 5px; font-size: 11px; }
        .items th { text-align: center; font-weight: 400; }
        .items .num-row td { text-align: center; font-size: 9.5px; padding: 1px; }
        .items td.c { text-align: center; }
        .items tr.blank td { height: 19px; }
        .items-title { text-align: center; font-weight: 700; margin-top: 14px; font-size: 11.5px; }

        /* --- Подписи --- */
        .sig-confirm { display: flex; align-items: flex-end; gap: 6px; margin-top: 18px; }
        .sig-confirm .line { flex: none; width: 200px; border-bottom: 1px solid #000; min-height: 15px; }
        .sig-block { margin-top: 14px; }
        .sig-row { display: flex; align-items: flex-end; gap: 8px; }
        .sig-role { flex: none; font-weight: 700; width: 128px; }
        .sig-line { flex: none; width: 150px; border-bottom: 1px solid #000; min-height: 15px; text-align: center; }
        .sig-caption { display: flex; gap: 8px; font-size: 8.5px; }
        .sig-caption .cap { text-align: center; }
        .stamp { font-weight: 400; margin: 3px 0 3px 24px; }

        @media print {
          body { background: #fff; }
          .no-print { display: none !important; }
          .poa-sheet { margin: 0; padding: 0; box-shadow: none; width: auto; }
        }
        @media screen {
          .poa-sheet { box-shadow: 0 0 8px rgba(0,0,0,0.2); }
        }
      `}</style>

      <div className="no-print" style={{ maxWidth: '210mm', margin: '16px auto 0', display: 'flex', gap: 8 }}>
        <button onClick={() => window.print()} style={{ padding: '6px 14px', cursor: 'pointer' }}>
          Печать
        </button>
        <button onClick={() => void handleDownload()} disabled={downloading} style={{ padding: '6px 14px', cursor: 'pointer' }}>
          {downloading ? 'Формирование PDF…' : 'Скачать PDF'}
        </button>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          Форма:
          <select value={form} onChange={(e) => setForm(e.currentTarget.value as FormKind)} style={{ padding: '5px 8px' }}>
            <option value="M2">М-2 (с корешком)</option>
            <option value="M2A">М-2а (без корешка)</option>
          </select>
        </label>
      </div>

      <div className="poa-sheet" ref={sheetRef}>
        {/* КОРЕШОК — только в форме М-2 */}
        {form === 'M2' && (
          <>
            <table className="stub-table">
              <tbody>
                <tr>
                  <td style={{ width: '11%' }}>Номер доверенности</td>
                  <td style={{ width: '11%' }}>Дата выдачи</td>
                  <td style={{ width: '10%' }}>Срок действия</td>
                  <td style={{ width: '48%' }}>Должность и фамилия лица, которому выдана доверенность</td>
                  <td style={{ width: '20%' }}>Расписка в получении доверенности</td>
                </tr>
                <tr className="num-row">
                  <td>1</td>
                  <td>2</td>
                  <td>3</td>
                  <td>4</td>
                  <td>5</td>
                </tr>
                <tr className="data-row">
                  <td className="c" style={{ textAlign: 'center' }}>
                    {poa.number}
                  </td>
                  <td style={{ textAlign: 'center' }}>{formatDateShort(poa.date)}</td>
                  <td style={{ textAlign: 'center' }}>{formatDateShort(poa.validUntil)}</td>
                  <td>
                    {[poa.employeePosition, poa.employeeName].filter(Boolean).join(', ')}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>

            <table className="stub-table" style={{ marginTop: -1 }}>
              <tbody>
                <tr>
                  <td style={{ width: '32%' }}>Поставщик</td>
                  <td style={{ width: '34%' }}>
                    Номер и дата наряда
                    <br />
                    (замещающего наряд документа) или извещения
                  </td>
                  <td style={{ width: '34%' }}>
                    Номер и дата документа, подтверждающего
                    <br />
                    выполнение поручения
                  </td>
                </tr>
                <tr className="num-row">
                  <td>6</td>
                  <td>7</td>
                  <td>8</td>
                </tr>
                <tr className="data-row">
                  <td>{poa.supplierName}</td>
                  <td>{basisDocument(poa)}</td>
                  <td>{poa.executedDocument ?? ''}</td>
                </tr>
              </tbody>
            </table>

            <div className="cut-line">Линия отреза</div>
          </>
        )}

        {/* ШАПКА ФОРМЫ */}
        <div className="form-meta">
          {meta.title}
          <br />
          Утверждена постановлением Госкомстата России от 30.10.97 № 71а
        </div>

        <div className="okud-block">
          <div className="okud-labels">
            <div style={{ height: 14 }} />
            <div>Форма по ОКУД</div>
            <div>по ОКПО</div>
          </div>
          <div className="okud-cells">
            <div className="cell">коды</div>
            <div className="value" style={{ borderBottom: '1px solid #000' }}>
              {meta.okud}
            </div>
            <div className="value">{org.okpo ?? ''}</div>
          </div>
        </div>

        {/* Графа «Организация» — две линии: наименование с ИНН и индексом,
            под ней остальной адрес. Заходит под блок ОКУД, поэтому поднята
            отрицательным отступом; подпись центрируется по обеим линиям */}
        <div className="f-row" style={{ marginTop: -30, alignItems: 'center' }}>
          <span className="f-label" style={{ width: 92 }}>
            Организация
          </span>
          <span className="f-fill" style={{ marginRight: 108 }}>
            <span className="f-value">{orgHead}</span>
            <span className="f-value">{orgAddress}</span>
            <span className="f-caption">наименование организации</span>
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 10 }}>
          <span className="doc-title" style={{ marginLeft: 60 }}>
            ДОВЕРЕННОСТЬ №
          </span>
          <span style={{ flex: 1, borderBottom: '1px solid #000', paddingLeft: 8, maxWidth: 320 }}>{poa.number}</span>
        </div>

        <div className="f-row" style={{ marginTop: 10 }}>
          <span className="f-label" style={{ width: 230, textAlign: 'right' }}>
            Дата выдачи:
          </span>
          <span className="f-fill" style={{ flex: 'none', width: 180 }}>
            <span className="f-value">{formatDateLong(poa.date)}</span>
          </span>
        </div>
        <div className="f-row" style={{ marginTop: 2 }}>
          <span className="f-label" style={{ width: 230, textAlign: 'right' }}>
            Доверенность действительна по:
          </span>
          <span className="f-fill" style={{ flex: 'none', width: 180 }}>
            <span className="f-value">{formatDateLong(poa.validUntil)}</span>
          </span>
        </div>

        <div className="f-row" style={{ marginTop: 10 }}>
          <span className="f-fill">
            <span className="f-value">{selfOneLine}</span>
            <span className="f-caption">наименование потребителя и его адрес</span>
          </span>
        </div>

        <div className="f-row" style={{ marginTop: 4 }}>
          <span className="f-fill">
            <span className="f-value">{selfOneLine}</span>
            <span className="f-caption">наименование плательщика и его адрес</span>
          </span>
        </div>

        {/* Банковские реквизиты одной строкой: расчётный счёт, банк, БИК и
            корсчёт. В бланке под них одна линия, разносить по строкам нельзя.
            Подпись выравнивается по центру значения, а не по низу линии:
            под линией идёт пояснение, и flex-end увёл бы её вниз */}
        <div className="f-row" style={{ alignItems: 'flex-start' }}>
          <span className="f-label" style={{ width: 92, textAlign: 'right', paddingTop: 1 }}>
            Счет №
          </span>
          <span className="f-fill">
            <span className="f-value">{bankDetails}</span>
            <span className="f-caption">наименование банка</span>
          </span>
        </div>

        <div className="f-row">
          <span className="f-label" style={{ width: 148 }}>
            Доверенность выдана
          </span>
          <span className="f-fill">
            <span className="f-value">{poa.employeePosition ?? ''}</span>
            <span className="f-caption">должность</span>
          </span>
          <span className="f-fill">
            <span className="f-value">{poa.employeeName}</span>
            <span className="f-caption">фамилия, имя, отчество</span>
          </span>
        </div>

        {/* Документ, удостоверяющий личность: у водителя это водительское
            удостоверение, у остальных — паспорт. Печатается тот, что заполнен */}
        <div className="f-row">
          <span className="f-label" style={{ width: 148, textAlign: 'right' }}>
            {identity.label}
          </span>
          <span className="f-fill" style={{ flex: 'none', width: 150 }}>
            <span className="f-value">{identity.series}</span>
          </span>
          <span className="f-label">№</span>
          <span className="f-fill">
            <span className="f-value">{identity.number}</span>
          </span>
        </div>

        <div className="f-row">
          <span className="f-label" style={{ width: 148, textAlign: 'right' }}>
            Кем выдан
          </span>
          <span className="f-fill">
            <span className="f-value">{identity.issuedBy}</span>
          </span>
        </div>

        <div className="f-row">
          <span className="f-label" style={{ width: 148, textAlign: 'right' }}>
            Дата выдачи
          </span>
          <span className="f-fill" style={{ flex: 'none', width: 200 }}>
            <span className="f-value">{identity.issueDate}</span>
          </span>
        </div>

        <div className="f-row" style={{ marginTop: 10 }}>
          <span className="f-label" style={{ width: 148, textAlign: 'right' }}>
            На получение от
          </span>
          <span className="f-fill">
            <span className="f-value">{poa.supplierName}</span>
            <span className="f-caption">наименование поставщика</span>
          </span>
        </div>

        <div className="f-row" style={{ marginTop: 2 }}>
          <span className="f-label" style={{ width: 148 }}>
            материальных ценностей по
          </span>
          <span className="f-fill">
            <span className="f-value">{basisDocument(poa)}</span>
            <span className="f-caption">наименование, номер и дата документа</span>
          </span>
        </div>

        {/* ПЕРЕЧЕНЬ ЦЕННОСТЕЙ */}
        <div className="items-title">Перечень товарно-материальных ценностей, подлежащих получению</div>

        <table className="items">
          <thead>
            <tr>
              <th style={{ width: 52 }}>
                Номер
                <br />
                по
                <br />
                порядку
              </th>
              <th>Материальные ценности</th>
              <th style={{ width: 62 }}>
                Единица
                <br />
                измерения
              </th>
              <th style={{ width: 190 }}>Количество (прописью)</th>
            </tr>
            <tr className="num-row">
              <td>1</td>
              <td>2</td>
              <td>3</td>
              <td>4</td>
            </tr>
          </thead>
          <tbody>
            {poa.lines.map((line) => (
              <tr key={line.id}>
                <td className="c">{line.lineNumber}</td>
                <td>{line.name}</td>
                <td className="c">{line.unitSymbol ?? ''}</td>
                <td>{formatQuantityCell(line.quantity)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ПОДПИСИ */}
        <div className="sig-confirm">
          <span>Подпись лица, получившего доверенность</span>
          <span className="line" />
          <span>удостоверяем.</span>
        </div>

        <div className="sig-block">
          <div className="sig-row">
            <span className="sig-role">Руководитель</span>
            <span className="sig-line" />
            <span className="sig-line">{abbreviateName(org.directorName)}</span>
          </div>
          <div className="sig-caption">
            <span style={{ width: 128 }} />
            <span className="cap" style={{ width: 150 }}>
              подпись
            </span>
            <span className="cap" style={{ width: 150, marginLeft: 8 }}>
              расшифровка подписи
            </span>
          </div>

          {/* В М-2 печать стоит между подписями, в М-2а — после руководителя */}
          <div className="stamp">М.П.</div>

          {/* Строка главбуха печатается всегда — она есть в типографском бланке.
              Если обязанности возложены на руководителя, расшифровку не
              дублируем: подпись ставится от руки */}
          <div className="sig-row">
            <span className="sig-role">Главный бухгалтер</span>
            <span className="sig-line" />
            <span className="sig-line">
              {org.accountantIsDirector ? '' : abbreviateName(org.accountantName)}
            </span>
          </div>
          <div className="sig-caption">
            <span style={{ width: 128 }} />
            <span className="cap" style={{ width: 150 }}>
              подпись
            </span>
            <span className="cap" style={{ width: 150, marginLeft: 8 }}>
              расшифровка подписи
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
